import React, { useContext, useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from "react-i18next";
import { ConfigContext } from '../../../contexts/config-context';
import { UserContext, profilePhoto } from '../../../contexts/userContext';
import ListingAPI from '../../../axios/listing';
import { formatNumber, LISTING_ROUTES } from '../home-utils';
import { SpecIcon } from '../../../components/shared-components/listing-icons';
import { ICON_PATHS } from '../../../components/shared-components/icon-paths';
import { floorLabel, numberLabel, plotAreaLabel } from '../../../components/shared-components/listing-forms';

const MAX_PROMOTED_PAGES = 3;
const MAX_CARDS = 8;
const NEW_LISTING_DAYS = 7;
// how far the mouse must move before a press becomes a drag instead of a click
const DRAG_THRESHOLD = 6;

// lets the track be dragged sideways with the mouse; touch screens already swipe it natively
const useMouseDrag = (trackRef) => {
    useEffect(() => {
        const el = trackRef.current;
        if (!el) return;
        let drag = null;

        const onDown = (e) => {
            if (e.pointerType !== 'mouse' || e.button !== 0) return;
            drag = { x: e.clientX, left: el.scrollLeft, moved: false };
        };
        const onMove = (e) => {
            if (!drag) return;
            const dx = e.clientX - drag.x;
            if (!drag.moved) {
                if (Math.abs(dx) < DRAG_THRESHOLD) return;
                drag.moved = true;
                el.classList.add('hs-dragging');
                el.setPointerCapture(e.pointerId);
                window.getSelection()?.removeAllRanges();
            }
            e.preventDefault();
            el.scrollLeft = drag.left - dx;
        };
        const onUp = () => {
            if (!drag) return;
            const moved = drag.moved;
            drag = null;
            if (!moved) return;
            // settle on the nearest card, then let snapping take over again
            const cards = Array.from(el.children);
            const nearest = cards.reduce((best, card) =>
                Math.abs(card.offsetLeft - el.offsetLeft - el.scrollLeft) < Math.abs(best.offsetLeft - el.offsetLeft - el.scrollLeft) ? card : best, cards[0]);
            el.classList.remove('hs-dragging');
            if (nearest) el.scrollTo({ left: nearest.offsetLeft - el.offsetLeft, behavior: 'smooth' });
            // the release that ends a drag must not open the card under the mouse
            const swallow = (ev) => { ev.stopPropagation(); ev.preventDefault(); };
            el.addEventListener('click', swallow, { capture: true, once: true });
            setTimeout(() => el.removeEventListener('click', swallow, { capture: true }), 0);
        };

        el.addEventListener('pointerdown', onDown);
        el.addEventListener('pointermove', onMove);
        el.addEventListener('pointerup', onUp);
        el.addEventListener('pointercancel', onUp);
        return () => {
            el.removeEventListener('pointerdown', onDown);
            el.removeEventListener('pointermove', onMove);
            el.removeEventListener('pointerup', onUp);
            el.removeEventListener('pointercancel', onUp);
        };
    }, [trackRef]);
};

export const AgencyIcon = () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="4" y="3" width="16" height="18" rx="1.5" /><path d="M9 7h2M13 7h2M9 11h2M13 11h2M9 15h2M13 15h2M10 21v-3h4v3" /></svg>
);

export const OwnerIcon = () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d={ICON_PATHS.user} /></svg>
);

// once users have saved the listing, their number is written to the left of the heart
export const LikeButton =({ listing, followers = 0, className = 'hs-card-like' }) => {
    const { user } = useContext(UserContext);
    const { t } = useTranslation(["home"]);
    const navigate = useNavigate();
    const [liked, setLiked] = useState(false);
    const [count, setCount] = useState(followers);

    useEffect(() => setCount(followers), [followers]);

    useEffect(() => {
        if (user) {
            ListingAPI.isListingLikedByUser(listing).then(res => setLiked(res.data.is_liked)).catch(() => { });
        }
    }, [user, listing]);

    const handleClick = (e) => {
        e.stopPropagation();
        if (!user) {
            navigate('/login');
            return;
        }
        const request = liked ? ListingAPI.unlikeListing(listing) : ListingAPI.likeListing(listing);
        request.then(() => {
            setLiked(!liked);
            setCount(current => Math.max(0, current + (liked ? -1 : 1)));
        }).catch(() => { });
    };

    return (
        <button className={`${className} ${count > 0 ? 'counted' : ''}`} onClick={handleClick} aria-label={t('save')} title={count > 0 ? t('followers', { n: count }) : undefined}>
            {count > 0 && <span className='hs-like-count'>{count}</span>}
            <svg width="18" height="18" viewBox="0 0 24 24" fill={liked ? '#FF5722' : 'none'} stroke={liked ? '#FF5722' : '#0E1A2B'} strokeWidth="2"><path d="M12 20.5s-7.5-4.6-9.3-9.3C1.4 7.8 3.6 4.5 7 4.5c2 0 3.6 1.1 5 3 1.4-1.9 3-3 5-3 3.4 0 5.6 3.3 4.3 6.7-1.8 4.7-9.3 9.3-9.3 9.3Z" /></svg>
        </button>
    );
};

// area, rooms and floor as icon tiles, those the listing has; shared by every listing card on the site
export const CardSpecs = ({ listing }) => {
    const { t } = useTranslation(["home", "listing"]);
    const area = Number(listing.area);
    // land has only its plot
    const plot = !area && plotAreaLabel(listing);
    const rooms = numberLabel('num_rooms', listing.num_rooms);
    const floor = floorLabel(listing, t)?.replace(' / ', '/');
    const specs = [
        area > 0 && ['area', `${area} m²`, t('area_short')],
        plot && ['area', plot, t('area_short')],
        rooms && ['rooms', rooms, t('rooms_short')],
        floor && ['floor', floor, t('floor_short')],
    ].filter(Boolean);
    if (specs.length === 0) return null;

    return (
        <div className='hs-card-specs'>
            {specs.map(([icon, value, label]) => (
                <span key={label} className='hs-card-spec' title={`${value} ${label}`}>
                    <SpecIcon name={icon} />
                    <b>{value}</b>
                    <em>{label}</em>
                </span>
            ))}
        </div>
    );
};

export const CardPublisher = ({ user }) => {
    const { api } = useContext(ConfigContext);
    const publisher = user?.user_type?.label;
    const isAgency = /agenc/i.test(publisher || '');
    const avatar = profilePhoto(user?.photo);

    return (
        <div className='hs-card-publisher'>
            <span className={`hs-card-publisher-icon ${isAgency ? 'agency' : ''}`} style={avatar ? { backgroundImage: `url(${api.host}:${api.port}${avatar})` } : undefined}>
                {!avatar && (isAgency ? <AgencyIcon /> : <OwnerIcon />)}
            </span>
            <span className='hs-card-publisher-text'>
                <span className='hs-card-publisher-name'>{[user?.first_name, user?.last_name].filter(Boolean).join(' ')}</span>
                <span className='hs-card-publisher-type'>{publisher}</span>
            </span>
        </div>
    );
};

export const FeaturedCard = ({ listing, className = '' }) => {
    const { api } = useContext(ConfigContext);
    const { t } = useTranslation(["home", "listing"]);
    const navigate = useNavigate();

    const { id, photos, price, area, title, place, subplace, location, promoted, promoted_value, luxurious, listing_type, user, date_created } = listing;
    const coverImage = photos?.length > 0
        ? `${api.host}:${api.port}${photos[0].photo.ratios.null.sources['image/webp']['500']}`
        : `${api.host}:${api.port}/media/No_Image_Available.jpg`;
    const isNew = (Date.now() - new Date(date_created).getTime()) < NEW_LISTING_DAYS * 24 * 60 * 60 * 1000;
    const isSale = listing_type?.id === 1;
    const priceSuffix = listing_type?.id === 2 ? ` ${t('price_unit_month')}` : listing_type?.id === 3 ? ` ${t('price_unit_night')}` : '';
    const locationText = [place?.label, subplace?.label, location?.label].filter(Boolean).join(' · ');

    return (
        <article className={`hs-card ${className}`} onClick={() => navigate(`/oglas/${id}`)}>
            <div className='hs-card-photo' style={{ backgroundImage: `url(${coverImage})` }}>
                <div className='hs-card-badges'>
                    {promoted && <span className='premium'>{promoted_value?.label || 'Premium'}</span>}
                    {luxurious && <span className='lux'>Lux</span>}
                    {isNew && <span className='new'>{t('badge_new')}</span>}
                </div>
                <LikeButton listing={id} followers={listing.followers} />
                <span className='hs-card-photo-count'>{photos?.length || 0} {t('photos')}</span>            </div>
            <div className='hs-card-body'>
                <div className='hs-card-price-row'>
                    <span className='hs-card-price'>{formatNumber(price)} €{priceSuffix}</span>
                    {isSale && area > 0 && <span className='hs-card-sub'>{formatNumber(price / area)} €/m²</span>}
                </div>
                <h3 className='hs-card-title' title={title}>{title}</h3>
                <p className='hs-card-loc' title={locationText}>{locationText}</p>
                <CardSpecs listing={listing} />
                <CardPublisher user={user} />
            </div>
        </article>
    );
};

// eyebrow/title let other pages (e.g. listing not found) reuse the slider under their own heading
export const FeaturedListings = ({ eyebrow, title }) => {
    const { t, i18n } = useTranslation(["home"]);
    const { searchData } = useContext(ConfigContext);
    const [listTab, setListTab] = useState(0);
    const [promoted, setPromoted] = useState(null);
    const [listings, setListings] = useState([]);
    const trackRef = useRef(null);
    const lang = i18n.language;
    useMouseDrag(trackRef);

    useEffect(() => {
        let cancelled = false;
        const fetchPromoted = async () => {
            try {
                const first = await ListingAPI.getPromotedList(1, lang);
                const pages = Math.min(first.data.page_count, MAX_PROMOTED_PAGES);
                const rest = await Promise.all(
                    Array.from({ length: pages - 1 }, (_, i) => ListingAPI.getPromotedList(i + 2, lang))
                );
                const all = [first, ...rest].flatMap(res => res.data.results);
                const unique = all.filter((l, i) => all.findIndex(o => o.id === l.id) === i);
                if (!cancelled) setPromoted(unique);
            } catch (error) {
                console.error("Error fetching promoted listings:", error);
                if (!cancelled) setPromoted([]);
            }
        };
        fetchPromoted();
        return () => { cancelled = true; };
    }, [lang]);

    useEffect(() => {
        if (promoted === null) return;
        let cancelled = false;
        const ofType = promoted.filter(l => l.listing_type?.id === listTab + 1);
        if (ofType.length > 0) {
            setListings(ofType.slice(0, MAX_CARDS));
        } else {
            // no promoted listings of this type, show the newest ones instead
            ListingAPI.getListingsSearch(1, { ...searchData, listing_type: String(listTab + 1) }, '-date_created', lang)
                .then(res => { if (!cancelled) setListings(res.data.results.slice(0, MAX_CARDS)); })
                .catch(error => {
                    console.error("Error fetching listings:", error);
                    if (!cancelled) setListings([]);
                });
        }
        if (trackRef.current) trackRef.current.scrollLeft = 0;
        return () => { cancelled = true; };
    }, [promoted, listTab, lang]);

    const slide = (dir) => {
        const el = trackRef.current;
        if (el) el.scrollBy({ left: dir * el.clientWidth, behavior: 'smooth' });
    };

    // the card nearest to the left edge; the dots under the track (phones only) follow it
    const [activeCard, setActiveCard] = useState(0);
    useEffect(() => setActiveCard(0), [listings]);
    const cardStart = (card) => card.offsetLeft - trackRef.current.offsetLeft;
    const onTrackScroll = () => {
        const el = trackRef.current;
        const cards = Array.from(el.children);
        // at the very end the last card can't reach the left edge, so it is marked once the track stops
        if (el.scrollLeft + el.clientWidth >= el.scrollWidth - 2) {
            setActiveCard(cards.length - 1);
            return;
        }
        const nearest = cards.reduce((best, card, i) =>
            Math.abs(cardStart(card) - el.scrollLeft) < Math.abs(cardStart(cards[best]) - el.scrollLeft) ? i : best, 0);
        setActiveCard(nearest);
    };
    const goToCard = (i) => {
        const card = trackRef.current?.children[i];
        if (card) trackRef.current.scrollTo({ left: cardStart(card), behavior: 'smooth' });
    };

    return (
        <section className='hs-section'>
            <div className='hs-section-head'>
                <div>
                    <p className='hs-eyebrow'>{eyebrow ?? t('featured_eyebrow')}</p>
                    <h2 className='hs-h2'>{title ?? t('featured_title')}</h2>
                </div>
                <div className='hs-section-tools'>
                    <div className='hs-pill-tabs'>
                        {[t('tab_sale'), t('tab_rent')].map((label, i) => (
                            <button key={label} className={i === listTab ? 'on' : ''} onClick={() => setListTab(i)}>{label}</button>
                        ))}
                    </div>
                    <Link to={LISTING_ROUTES[listTab]} className='hs-more-link' onClick={() => sessionStorage.removeItem('currentPage')}>{t('see_all')} →</Link>
                    <div className='hs-arrows'>
                        <button onClick={() => slide(-1)} aria-label={t('prev')}>←</button>
                        <button onClick={() => slide(1)} aria-label={t('next')}>→</button>
                    </div>
                </div>
            </div>
            <div ref={trackRef} className='hs-card-track' onScroll={onTrackScroll}>
                {listings.map(listing => <FeaturedCard listing={listing} key={`featured-${listing.id}`} />)}
            </div>
            {listings.length > 1 &&
                <div className='hs-card-dots'>
                    {listings.map((listing, i) => (
                        <button key={`dot-${listing.id}`} className={i === activeCard ? 'on' : ''}
                            onClick={() => goToCard(i)} aria-label={`${i + 1} / ${listings.length}`} />
                    ))}
                </div>}
        </section>
    );
};
